'use client';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import NextImage from 'next/image';
import { motion, AnimatePresence } from 'motion/react';
import { useScrollLock } from 'usehooks-ts';
import { cn } from 'lib/utils';
import { Bloom, Button, Icon, Link } from 'ui';
import { type GlobalHeaderProps } from './index';

interface GlobalHeaderMobileProps extends GlobalHeaderProps {
  isSticky?: boolean;
}

const CONTACT_US_TRACKING_PROPS = {
  event: 'CTA_clicked',
  properties: {
    cta: 'Contact Us',
    location: 'global nav',
    path: '/contact',
    action: 'go to contact us',
  },
};

const GlobalHeaderMobile = ({
  ctaLink,
  isSticky,
  items,
}: GlobalHeaderMobileProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const panelId = useId();
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Use scroll lock hook to prevent background scrolling when menu is open
  const { lock: lockScroll, unlock: unlockScroll } = useScrollLock({
    autoLock: false,
    lockTarget: 'html',
    // Gutter is reserved globally via `scrollbar-gutter: stable` (base.css),
    // so skip the JS padding compensation to avoid double-compensating.
    widthReflow: false,
  });

  const handleToggleClick = () => {
    setIsOpen((prev) => !prev);
    if (isOpen) {
      setActiveDropdown(null);
    }
  };

  const closeMenu = useCallback((restoreFocus = true) => {
    setIsOpen(false);
    setActiveDropdown(null);
    // Only restore focus when explicitly requested (user action, not system event)
    if (restoreFocus) {
      setTimeout(() => {
        menuButtonRef.current?.focus();
      }, 100);
    }
  }, []);

  const closeMenuWithoutFocus = useCallback(() => {
    closeMenu(false);
  }, [closeMenu]);

  const handleCloseWithFocus = useCallback(() => {
    closeMenu(true);
  }, [closeMenu]);

  const handleDropdownClick = (index: number) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  // Handle escape key and window resize
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        closeMenu(true); // User action - restore focus
      }
    };

    const handleResize = () => {
      if (isOpen) {
        closeMenuWithoutFocus(); // System event - don't restore focus
      }
    };

    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, closeMenu, closeMenuWithoutFocus]);

  // Prevent body scroll when menu is open and scroll to top
  useEffect(() => {
    if (isOpen) {
      // Smooth scroll to top when menu opens
      // window.scrollTo({
      //   top: 0,
      //   behavior: 'smooth',
      // });

      // Lock body scroll
      lockScroll();
    } else {
      // Unlock body scroll
      unlockScroll();
    }

    // Cleanup on unmount
    return () => {
      unlockScroll();
    };
  }, [isOpen, lockScroll, unlockScroll]);

  return (
    <>
      {/* Live region for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {isOpen ? 'Navigation menu opened' : ''}
      </div>

      <div className="lg:hidden z-20 absolute left-0 top-full w-full h-header-height-mobile">
        <div className="h-full pt-2 px-2">
          {/* Inner Wrapper */}
          <div
            className={cn(
              'h-full flex items-center justify-between px-4 rounded-lg backdrop-blur-xl overflow-hidden transition-colors duration-300',
              {
                'bg-graphite-900': isOpen,
              }
            )}
          >
            {/* Logo */}
            <Link
              href="/"
              className="flex rounded focus-visible:outline-2 focus-visible:outline-offset-5 focus-visible:outline-athletic-500"
              aria-label="Navigate to homepage"
              onClick={handleCloseWithFocus}
            >
              <NextImage
                src="/_assets/header-logo-mobile.svg"
                alt="Oura for Organizations"
                width={116}
                height={34}
                className="w-29 h-8.5"
              />
            </Link>

            {/* Mobile Menu Button */}
            <button
              ref={menuButtonRef}
              type="button"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
              aria-controls={isOpen ? `${panelId}-menu` : undefined}
              className="flex items-center justify-center size-10 rounded-full bg-transparent hover:bg-clay5 active:bg-clay5 border border-clay-50/25 focus-visible:outline-2 focus-visible:outline-offset-5 focus-visible:outline-athletic-500 cursor-pointer"
              onClick={handleToggleClick}
            >
              <div
                className="relative size-5 flex flex-col justify-center items-center"
                aria-hidden="true"
              >
                <span
                  className={cn(
                    'block h-px w-full bg-clay-50 transition-all duration-300 ease-out',
                    isOpen ? 'rotate-45 translate-y-0' : '-translate-y-0.5'
                  )}
                />
                <span
                  className={cn(
                    'block h-px w-full bg-clay-50 transition-all duration-300 ease-out',
                    isOpen ? '-rotate-45 -translate-y-px' : 'translate-y-0.5'
                  )}
                />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Below Header */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id={`${panelId}-menu`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className={cn(
              'lg:hidden fixed inset-0 z-10 w-full bg-graphite-950',
              {
                'pt-mobile-menu-top-offset': !isSticky,
                'pt-header-height-mobile': isSticky,
              }
            )}
          >
            <Bloom
              size="lg"
              positionX="right"
              positionY="bottom"
              variant="blue"
            />

            <div className="h-full overflow-y-auto pt-2 pb-6 px-6">
              <nav
                className="border-t border-clay-50/15"
                role="navigation"
                aria-label="Mobile navigation"
              >
                <h2 className="sr-only">Navigation Menu</h2>
                {items.map((item, index) => (
                  <div key={index}>
                    {'href' in item ? (
                      // Simple link
                      <div onClick={handleCloseWithFocus}>
                        <Link
                          href={item.href}
                          // target={item.target}
                          rel={item.rel}
                          className="block py-6 px-0 text-p1 font-bold no-underline border-b border-clay-50/15 hover:bg-clay-50/5 transition-colors"
                        >
                          {item.text}
                        </Link>
                      </div>
                    ) : (
                      // Dropdown section
                      <div>
                        <button
                          onClick={() => handleDropdownClick(index)}
                          aria-expanded={activeDropdown === index}
                          aria-haspopup="true"
                          aria-controls={
                            activeDropdown === index
                              ? `${panelId}-submenu-${index}`
                              : undefined
                          }
                          className="group w-full flex items-center justify-between py-6 px-0 text-p1 font-bold border-b border-clay-50/15 hover:text-athletic-200 transition-colors text-left focus-visible:outline-2 focus-visible:outline-offset-5 focus-visible:outline-athletic-500 focus-visible:rounded cursor-pointer"
                        >
                          <span>{item.label}</span>
                          <Icon
                            name="chevron-down"
                            className={cn(
                              'h-2 w-auto fill-clay-50 group-hover:fill-athletic-200 transition-transform duration-200',
                              {
                                'scale-y-[-1]': activeDropdown === index,
                              }
                            )}
                            aria-hidden="true"
                          />
                        </button>

                        <AnimatePresence>
                          {activeDropdown === index && (
                            <motion.div
                              id={`${panelId}-submenu-${index}`}
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.25, ease: 'easeOut' }}
                              className="overflow-hidden"
                            >
                              <div className="pt-6 space-y-4">
                                {/* CTA Section */}
                                <div>
                                  <h3 className="text-p2 font-bold mb-2">
                                    {item.panelTitle}
                                  </h3>
                                  <p className="text-p3 text-neutral-200">
                                    {item.panelIntro}
                                  </p>
                                </div>

                                {/* Links */}
                                <div>
                                  {item.children.map((child, childIndex) => (
                                    <motion.div
                                      key={childIndex}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{
                                        duration: 0.2,
                                        delay: childIndex * 0.05,
                                      }}
                                    >
                                      <div onClick={handleCloseWithFocus}>
                                        <Link
                                          href={child.link.href}
                                          // target={child.link.target}
                                          rel={child.link.rel}
                                          className="group block py-6 transition-colors no-underline text-clay-50 hover:text-athletic-200 border-b border-clay-50/20 focus-visible:outline-2 focus-visible:outline-offset-5 focus-visible:outline-athletic-500"
                                          trackingProps={
                                            child.link.trackingProps
                                          }
                                        >
                                          <span className="flex flex-col gap-2">
                                            <span className="flex gap-1.5 items-start">
                                              {child.icon && (
                                                <span className="size-4.5 flex items-center justify-center shrink-0 mt-0.5">
                                                  <Icon
                                                    name={child.icon}
                                                    className="size-auto max-w-full max-h-full fill-clay-50 group-hover:fill-athletic-200"
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              )}

                                              <span className="block font-bold">
                                                <span>{child.link.text}</span>
                                                {child.link.target ===
                                                  '_blank' && (
                                                  <Icon
                                                    name="external-link-arrow"
                                                    className="align-middle inline-block w-2.5 h-auto fill-clay-50 group-hover:fill-athletic-200 ml-2"
                                                    aria-hidden="true"
                                                  />
                                                )}
                                              </span>
                                            </span>

                                            <span className="block text-p3 text-neutral-200">
                                              {child.description}
                                            </span>
                                          </span>
                                        </Link>
                                      </div>
                                    </motion.div>
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                ))}

                {/* CTA Button */}
                <div className="pt-6 flex items-center justify-center text-center">
                  <div onClick={handleCloseWithFocus}>
                    <Button
                      href={ctaLink.href}
                      trackingProps={CONTACT_US_TRACKING_PROPS}
                    >
                      {ctaLink.text}
                    </Button>
                  </div>
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GlobalHeaderMobile;
